import { useState } from "react";
import { faMap } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";

import type { MvcLead } from "@repo/models";
import { dateOf, fullAddress, name } from "@repo/helpers";
import { Button, ButtonColor, filterPositionsWithinSquare, Grid, MapChart } from "@repo/ui";
import { Column } from "@repo/ui/grid/models/column";
import { DateColumn } from "@repo/ui/grid/models/dateColumn";

import type { LeadsFilter } from "../../api/leadsController";
import { BreadcrumbsController } from "../../api/breadcrumbsController";
import { LeadsController } from "../../api/leadsController";
import { MarkerMap } from "../maps/map";

const COLUMNS = [
	new Column("Lead Id", "id"),
	new DateColumn("Created On", "createdOn"),
	new Column("Rep Name").withFormat((v: MvcLead) => name(v.repFirstName, v.repLastName)),
	new Column("Rep Login", "repLogin"),
	new Column("Office", "branch"),
	new Column("Channel", "channel"),
	new Column("State", "state"),
	new Column("Converted").withFormat((i: MvcLead) =>
		i.latestEnrollment === null ? (
			"No"
		) : (
			<a href={`/MVC/EnrollmentMaster/ResiDtd?enrollmentId=${i.latestEnrollment.enrollmentId}`} target="_blank">
				{i.latestEnrollment.enrollmentId}
			</a>
		),
	),
	new Column("Disposition").withFormat((v: MvcLead) => v.latestEnrollment?.tpvDisposition ?? ""),
	new Column("Date of Sale").withFormat((i) => dateOf(i.latestEnrollment?.dateOfSale)),
	new Column("Customer Name").withFormat((v: MvcLead) =>
		name(v.latestEnrollment?.customerFirstName ?? "", v.latestEnrollment?.customerLastName ?? ""),
	),
	new Column("Customer Address").withFormat((v: MvcLead) =>
		fullAddress(
			v.latestEnrollment?.customerAddress.address1 ?? "",
			v.latestEnrollment?.customerAddress.address2 ?? "",
			v.latestEnrollment?.customerAddress.city ?? "",
			v.latestEnrollment?.customerAddress.state ?? "",
			v.latestEnrollment?.customerAddress.zip ?? "",
		),
	),
	new Column("Product").withFormat((v: MvcLead) =>
		v.latestEnrollment?.productOffer == null
			? ""
			: `${v.latestEnrollment?.productOffer.name} (${v.latestEnrollment?.productOffer.description})`,
	),
] as Column<MvcLead>[];

interface Props {
	filters: LeadsFilter;
	setFilters: (filters: LeadsFilter) => void;
}

export function LeadsGrid(props: Props) {
	const [expanded, setExpanded] = useState<number | null>(null);

	const leadsQuery = useQuery({
		queryKey: ["leads", props.filters],
		queryFn: () => LeadsController.getLeads(props.filters),
	});

	const retailLocationsQuery = useQuery({
		queryKey: ["retailLocations"],
		queryFn: () => BreadcrumbsController.getRetailLocations(),
	});

	const handleRowClick = (id: number) => {
		if (expanded === id) setExpanded(null);
		else setExpanded(id);
	};

	const columns = [
		new Column("Map", "id").withFormat((v) => (
			<Button color={ButtonColor.Green} onClick={() => handleRowClick(v)}>
				<FontAwesomeIcon icon={faMap} color="white" />
			</Button>
		)),
		...COLUMNS,
	];

	function getExpandedDisplay(id: number) {
		if (id !== expanded) return <></>;
		if (expanded === null) return <></>;

		const lead = leadsQuery.data?.items.find((l: MvcLead) => l.id === id);

		if (!lead) return <></>;

		const filteredPositions = filterPositionsWithinSquare(
			lead.lat,
			lead.lng,
			retailLocationsQuery.data.map((item) => ({
				lat: item.lat,
				lon: item.lng,
				...item,
			})),
		);

		return (
			<div className="tw-h-[400px]">
				<MapChart
					data={[
						...filteredPositions.map(MarkerMap.retailLocation),
						{
							id: lead.id,
							datetime: new Date(),
							lat: lead.lat ?? 0,
							lon: lead.lng ?? 0,
							tooltip: `${lead.repLogin} - ${name(lead.repFirstName, lead.repLastName)}`,
							label: [
								{
									repName: name(lead.repFirstName, lead.repLastName),
									repLogin: lead.repLogin,
								},
							],
							image: "/Content/Map/map-pin-red.png",
						},
					]}
					type="map"
					mapType="satellite"
					id={""}
					title={""}
				/>
			</div>
		);
	}

	async function getRecordsToExport() {
		return await LeadsController.getLeadsForExport(props.filters);
	}

	return (
		<Grid<MvcLead>
			id="leadsGrid"
			title="Leads"
			keyProperty="id"
			sortable
			changeVisibility
			resizeable
			page
			refresh={leadsQuery.refetch}
			loading={leadsQuery.isFetching}
			pageSize={props.filters.itemsPerPage}
			totalItems={leadsQuery.data?.totalItems ?? 0}
			pageSizes={[25, 50, 100, 500]}
			currentPage={props.filters.currentPage}
			values={leadsQuery.data?.items ?? []}
			columns={columns}
			canSetItemsPerPage
			pageChanged={(page) => {
				props.setFilters({ ...props.filters, currentPage: page });
			}}
			setItemsPerPage={(n) => {
				props.setFilters({ ...props.filters, itemsPerPage: n });
			}}
			expandedDisplay={getExpandedDisplay}
			exportable
			apiRoute="Leads"
			getRecordsToExport={getRecordsToExport}
		/>
	);
}
